import { OpenAPI } from '../api/core/OpenAPI';
import { store } from '../redux/store';
import type { MyActivityResponse, MyActivitySalesTrendPoint } from '../types/myActivity.types';

/** GET autenticado a /dashboard/<path>; lanza Error con el mensaje del backend si falla. */
async function getDashboardJson<T>(
  path: string,
  params: Record<string, string>,
  signal?: AbortSignal,
): Promise<T> {
  const apiBaseUrl = OpenAPI.BASE;
  const token = store.getState().auth.token ?? '';

  if (token === '') {
    throw new Error('No se encontró token de autenticación.');
  }

  const url = new URL(`${apiBaseUrl}/dashboard/${path}`);
  Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      message: 'Error desconocido',
    }));
    throw new Error(
      (errorData as { message?: string }).message ??
        `Error ${response.status}: ${response.statusText}`,
    );
  }

  return (await response.json()) as T;
}

/**
 * Inicio "Mi actividad": today's counters (range in the client's timezone) + last 5 sales
 * of the logged-in user.
 */
export async function fetchMyActivity(
  range: { from: string; to: string },
  signal?: AbortSignal,
): Promise<MyActivityResponse> {
  const data = await getDashboardJson<MyActivityResponse>(
    'my-activity',
    { from: range.from, to: range.to },
    signal,
  );
  return {
    today: {
      salesCount: Number(data?.today?.salesCount ?? 0),
      piecesSold: Number(data?.today?.piecesSold ?? 0),
      pendingPayment: Number(data?.today?.pendingPayment ?? 0),
    },
    recentSales: (data?.recentSales ?? []).map((s) => ({
      ...s,
      totalAmount: Number(s.totalAmount),
      itemsCount: Number(s.itemsCount),
    })),
  };
}

/**
 * Inicio "Mi semana" (últimos 7 días): the logged-in user's own sales per local day.
 * The backend groups by day in `tz`, so a late-night sale lands on the right day.
 */
export async function fetchMyActivitySalesTrend(
  range: { from: string; to: string; tz: string },
  signal?: AbortSignal,
): Promise<MyActivitySalesTrendPoint[]> {
  const data = await getDashboardJson<MyActivitySalesTrendPoint[]>(
    'my-activity/sales-trend',
    { from: range.from, to: range.to, tz: range.tz },
    signal,
  );
  return (Array.isArray(data) ? data : []).map((p) => ({
    date: p.date,
    orders: Number(p.orders ?? 0),
    pieces: Number(p.pieces ?? 0),
    total: Number(p.total ?? 0),
  }));
}
